import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';

import { getLatestPageName } from 'store/pageLoading/selectors';
import { updateScrollPosition } from 'store/providerList/actions';
import { getScrollPosition } from 'store/providerList/selectors';

function useInitialScrollPosition(isFetchingList: boolean) {
  const dispatch = useDispatch();
  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const timerRef = useRef<NodeJS.Timeout | number>(0);

  const previousPageName = useSelector(getLatestPageName);
  const scrollPosition = useSelector(getScrollPosition);
  const isBackActionRef = useRef(false);

  const onScroll = useCallback((position?: number) => {
    clearTimeout(timerRef.current as number);
    timerRef.current = setTimeout(() => {
      window.scrollTo({
        top: position || window.innerHeight - 64, // adjust with respect to design
        left: 0,
        behavior: position === undefined ? 'smooth' : 'auto',
      });
    }, 40);
  }, []);

  useEffect(() => {
    if (!isFetchingList) {
      if (previousPageName === 'providerInfo') {
        isBackActionRef.current = true;
        onScroll(scrollPosition);
      } else if (!isBackActionRef.current && isMobile) {
        onScroll();
      }
    }
  }, [isFetchingList, isMobile, onScroll, previousPageName, scrollPosition]);

  useEffect(() => {
    let currentPageYOffset = 0;

    function savePositionOnScroll() {
      currentPageYOffset = window.pageYOffset;
    }

    document.addEventListener('scroll', savePositionOnScroll);
    return () => {
      clearTimeout(timerRef.current as number);
      dispatch(updateScrollPosition(currentPageYOffset));
      document.removeEventListener('scroll', savePositionOnScroll);
    };
  }, [dispatch]);

  return { onDefaultScroll: onScroll };
}

export default useInitialScrollPosition;
